import type { DriveStep, Driver } from 'driver.js';

/**
 * Attaches a one-shot click listener that advances the driver tour
 * after a short delay, used by CreationGuide steps where the user
 * interaction (e.g. opening a sheet) needs time to settle before
 * the next step's target appears in the DOM.
 */
function advanceOnClick(element: Element | undefined, drv: Driver): void {
  if (!(element instanceof HTMLElement)) return;
  element.addEventListener(
    'click',
    () => {
      if (drv.isActive()) setTimeout(() => drv.moveNext(), 350);
    },
    { once: true },
  );
}

/**
 * Returns an `onHighlightStarted` hook that programmatically activates
 * the tab whose trigger matches `triggerSelector`. Used by Overview
 * tours to traverse tabs without requiring user interaction: the user
 * only clicks "Avanti" on the tooltip and the tab switches itself.
 */
function activateTab(triggerSelector: string): DriveStep['onHighlightStarted'] {
  return () => {
    document.querySelector<HTMLElement>(triggerSelector)?.click();
  };
}

/**
 * A tour is a sequence of driver.js steps plus optional global hooks.
 */
export interface Tour {
  readonly steps: readonly DriveStep[];
  /** Halo padding around the highlighted element, in px. */
  readonly stagePadding?: number;
  /** Hook fired once before the tour starts (e.g. to prepare DOM). */
  readonly onBeforeStart?: () => void;
}

/**
 * Stable identifiers for the supported tour variants on a route.
 * Extend as new variants are introduced.
 */
export enum TutorialId {
  Overview = 'overview',
  CreationGuide = 'creation-guide',
  DetailGuide = 'detail-guide',
}

/**
 * User-facing labels for the variants, shown in the picker dialog.
 */
export enum TutorialLabel {
  Overview = 'Panoramica',
  CreationGuide = 'Guida alla creazione',
  DetailGuide = 'Guida al dettaglio',
}

/**
 * A tour variant attached to a route.
 */
export interface PageTutorial extends Tour {
  readonly id: TutorialId;
  readonly label: TutorialLabel;
  /** Short description shown in the picker. */
  readonly description?: string;
  /**
   * Optional CSS selector that gates the variant: when the element is
   * not in the DOM (e.g. user lacks permission), the variant is hidden
   * from the picker.
   */
  readonly requiresElement?: string;
}

/**
 * Tutorial configuration for a route prefix.
 * The longest matching prefix wins, so nested routes can override parents.
 */
export interface TutorialConfig {
  readonly routePrefix: string;
  readonly dialogTitle: string;
  readonly dialogDescription: string;
  readonly tutorials: readonly PageTutorial[];
}

/**
 * Shared dialog description used by the picker across all routes.
 */
const DEFAULT_DIALOG_DESCRIPTION =
  'Scegli la guida interattiva da avviare. Puoi interromperla in qualsiasi momento.';

/**
 * Route-bound tutorial seed. Each entry binds a route prefix to a
 * dialog title/description and a list of tutorial variants. The
 * `TutorialService` matches the current URL against `routePrefix`
 * (longest prefix wins) and filters steps whose `element` is not in
 * the DOM, so role- or tab-gated UI is handled automatically.
 *
 * Selectors are component tag names (`app-page-header`,
 * `app-{feature}-actions`, `app-{feature}-table`) so no template
 * edits are needed.
 */
export const TUTORIAL_SEED: TutorialConfig[] = [
  {
    routePrefix: '/payslips',
    dialogTitle: 'Tutorial · Cedolini',
    dialogDescription: DEFAULT_DIALOG_DESCRIPTION,
    tutorials: [
      {
        id: TutorialId.Overview,
        label: TutorialLabel.Overview,
        steps: [
          {
            element: 'app-page-header',
            popover: {
              title: 'Benvenuto nei Cedolini',
              description:
                'Da qui consulti, scarichi e gestisci i cedolini del mese corrente e quelli storici.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: 'app-payslips-actions',
            popover: {
              title: 'Cerca e filtra',
              description:
                'Trova rapidamente un cedolino con la ricerca o filtra per mese e anno. Puoi anche scegliere quali colonne mostrare.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: '[hlmTabsTrigger="latest"]',
            popover: {
              title: 'Mese corrente',
              description:
                'Qui trovi i cedolini del mese in corso, pronti da scaricare o inviare.',
              side: 'bottom',
              align: 'start',
            },
            onHighlightStarted: activateTab('[hlmTabsTrigger="latest"]'),
          },
          {
            element: '[hlmTabsTrigger="history"]',
            popover: {
              title: 'Storico',
              description:
                'Accedi a tutti i cedolini dei mesi precedenti con la stessa interfaccia.',
              side: 'bottom',
              align: 'start',
            },
            onHighlightStarted: activateTab('[hlmTabsTrigger="history"]'),
          },
          {
            element: 'app-payslips-table',
            popover: {
              title: 'La tua lista di cedolini',
              description:
                'Ogni riga è un cedolino: aprilo per vedere il dettaglio o usa il menu a destra per scaricarlo, inviarlo o eliminarlo.' +
                ' Puoi anche inviare tutti i cedolini ai tuoi dipendenti con un solo click sul pulsante d\'invio in alto oppure selezionare quali inviare tramite le checkbox a sinistra.',
              side: 'top',
              align: 'center',
            },
            onHighlightStarted: activateTab('[hlmTabsTrigger="latest"]'),
          },
        ],
      },
      {
        id: TutorialId.CreationGuide,
        label: TutorialLabel.CreationGuide,
        description: 'Ti guidiamo nel caricamento di un nuovo lotto di cedolini.',
        requiresElement: '[data-tour="payslips-upload"]',
        steps: [
          {
            element: '[data-tour="payslips-upload"]',
            popover: {
              title: 'Apri il pannello di caricamento',
              description:
                'Clicca «Carica Cedolini»: il tour avanzerà da solo non appena il pannello si apre.',
              side: 'bottom',
              align: 'end',
              showButtons: ['close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
          {
            element: '[data-tour="upload-month"]',
            popover: {
              title: 'Seleziona il mese',
              description: 'Il mese di riferimento dei cedolini. Di default è il mese precedente.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="upload-year"]',
            popover: {
              title: 'Seleziona l\'anno',
              description: 'L\'anno di riferimento. Sono disponibili l\'anno corrente e i due precedenti.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="upload-dropzone"]',
            popover: {
              title: 'Carica i PDF',
              description:
                'Clicca per selezionare uno o più PDF dei cedolini. Se selezioni più file verranno uniti automaticamente in un unico documento.',
              side: 'left',
              align: 'center',
            },
          },
          {
            element: '[data-tour="upload-info"]',
            popover: {
              title: 'Estrazione automatica',
              description:
                'I cedolini vengono analizzati dall\'AI per estrarre i dati dei dipendenti e associarli correttamente.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="upload-submit"]',
            popover: {
              title: 'Avvia il caricamento',
              description:
                'Clicca «Carica» per confermare. Quando l\'elaborazione è completata vedrai i cedolini comparire nell\'elenco.',
              side: 'left',
              align: 'end',
              showButtons: ['previous', 'close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
        ],
      },
    ],
  },
  {
    routePrefix: '/expense-reports',
    dialogTitle: 'Tutorial · Rimborsi',
    dialogDescription: DEFAULT_DIALOG_DESCRIPTION,
    tutorials: [
      {
        id: TutorialId.Overview,
        label: TutorialLabel.Overview,
        steps: [
          {
            element: 'app-page-header',
            popover: {
              title: 'Benvenuto nei Rimborsi',
              description:
                'Da qui visualizzi e gestisci le note spese, con approvazione o rifiuto se sei manager.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: 'app-expense-reports-actions',
            popover: {
              title: 'Filtra le note spese',
              description:
                'Usa la ricerca o i filtri per categoria, mese e stato. Reimposta i filtri con un click.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: 'app-expense-reports-table',
            popover: {
              title: 'L\'elenco delle note spese',
              description:
                'Ogni riga è una nota spesa: aprila per il dettaglio o approvala o rifiutala dal menu a destra.',
              side: 'top',
              align: 'center',
            },
          },
        ],
      },
      {
        id: TutorialId.CreationGuide,
        label: TutorialLabel.CreationGuide,
        description: 'Ti guidiamo nella creazione di una nuova nota spesa.',
        requiresElement: '[data-tour="expense-reports-create"]',
        steps: [
          {
            element: '[data-tour="expense-reports-create"]',
            popover: {
              title: 'Crea una nuova nota spesa',
              description:
                'Clicca «Nuovo Rimborso»: il tour avanzerà da solo non appena il pannello si apre.',
              side: 'bottom',
              align: 'end',
              showButtons: ['close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
          {
            element: '[data-tour="expense-employee"]',
            popover: {
              title: 'Dipendente',
              description: 'Seleziona il dipendente a cui è associata la spesa.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="expense-category"]',
            popover: {
              title: 'Categoria',
              description:
                'Scegli la categoria della spesa (es. carburante, vitto, trasporti). Per le spese di carburante compariranno campi aggiuntivi.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="expense-amount"]',
            popover: {
              title: 'Importo',
              description: 'Inserisci l\'importo della spesa e la valuta.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="expense-date"]',
            popover: {
              title: 'Data',
              description: 'Data in cui la spesa è stata sostenuta.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="expense-description"]',
            popover: {
              title: 'Descrizione',
              description:
                'Campo opzionale per aggiungere dettagli o note sulla spesa (es. cliente, motivo della trasferta).',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="expense-save"]',
            popover: {
              title: 'Conferma',
              description: 'Clicca «Crea» per salvare la nota spesa.',
              side: 'left',
              align: 'end',
              showButtons: ['previous', 'close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
        ],
      },
    ],
  },
  {
    routePrefix: '/leave-requests',
    dialogTitle: 'Tutorial · Presenze e Assenze',
    dialogDescription: DEFAULT_DIALOG_DESCRIPTION,
    tutorials: [
      {
        id: TutorialId.Overview,
        label: TutorialLabel.Overview,
        steps: [
          {
            element: 'app-page-header',
            popover: {
              title: 'Benvenuto in Presenze e Assenze',
              description:
                'Da qui consulti e gestisci le richieste di ferie, permessi e altre assenze.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: 'app-leave-requests-actions',
            popover: {
              title: 'Cerca e filtra',
              description:
                'Filtra per categoria, data di inizio o durata. La ricerca lavora sui nomi dei dipendenti.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: 'app-leave-requests-table',
            popover: {
              title: 'Le richieste di assenza',
              description:
                'Ogni riga è una richiesta. Se sei manager puoi approvarle o rifiutarle singolarmente o in blocco selezionando più righe.',
              side: 'top',
              align: 'center',
            },
          },
        ],
      },
      {
        id: TutorialId.CreationGuide,
        label: TutorialLabel.CreationGuide,
        description: 'Ti guidiamo nella creazione di una nuova richiesta di assenza.',
        requiresElement: '[data-tour="leave-requests-create"]',
        steps: [
          {
            element: '[data-tour="leave-requests-create"]',
            popover: {
              title: 'Crea una richiesta',
              description:
                'Clicca «Nuova Richiesta»: il tour avanzerà da solo non appena il pannello si apre.',
              side: 'bottom',
              align: 'end',
              showButtons: ['close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
          {
            element: '[data-tour="leave-employee"]',
            popover: {
              title: 'Dipendente',
              description: 'Seleziona il dipendente a cui è intestata la richiesta.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="leave-category"]',
            popover: {
              title: 'Tipologia',
              description:
                'Ferie, permesso, malattia, trasferta o altro. La tipologia può abilitare campi aggiuntivi.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="leave-duration"]',
            popover: {
              title: 'Durata',
              description:
                'Scegli se la richiesta copre un singolo giorno, più giorni consecutivi, o un intervallo orario. La selezione cambia i campi mostrati sotto.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="leave-date"]',
            popover: {
              title: 'Data',
              description:
                'Indica il giorno o l\'intervallo di giorni dell\'assenza. Per i permessi orari compariranno campi per inizio e fine.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="leave-notes"]',
            popover: {
              title: 'Note',
              description: 'Aggiungi un motivo o una nota opzionale per chi dovrà approvare.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="leave-save"]',
            popover: {
              title: 'Conferma',
              description: 'Clicca «Crea» per inviare la richiesta. Sarà inoltrata a chi può approvarla.',
              side: 'left',
              align: 'end',
              showButtons: ['previous', 'close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
        ],
      },
    ],
  },
  {
    routePrefix: '/documents',
    dialogTitle: 'Tutorial · Documenti',
    dialogDescription: DEFAULT_DIALOG_DESCRIPTION,
    tutorials: [
      {
        id: TutorialId.Overview,
        label: TutorialLabel.Overview,
        steps: [
          {
            element: 'app-page-header',
            popover: {
              title: 'Benvenuto nei Documenti',
              description:
                'Da qui consulti e gestisci i documenti aziendali condivisi e quelli personali dei dipendenti.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: 'app-documents-actions',
            popover: {
              title: 'Cerca i documenti',
              description:
                'Filtra per tipo di documento o usa la ricerca per nome file. Personalizza le colonne visibili.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: 'app-documents-table',
            popover: {
              title: 'L\'elenco dei documenti',
              description:
                'Ogni riga è un documento: scaricalo dal menu a destra o eliminalo se ne hai i permessi.',
              side: 'top',
              align: 'center',
            },
          },
        ],
      },
      {
        id: TutorialId.CreationGuide,
        label: TutorialLabel.CreationGuide,
        description: 'Ti guidiamo nel caricamento di un nuovo documento.',
        requiresElement: '[data-tour="documents-create"]',
        steps: [
          {
            element: '[data-tour="documents-create"]',
            popover: {
              title: 'Carica un documento',
              description:
                'Clicca «Carica Documento»: il tour avanzerà da solo non appena il pannello si apre.',
              side: 'bottom',
              align: 'end',
              showButtons: ['close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
          {
            element: '[data-tour="document-type"]',
            popover: {
              title: 'Tipo documento',
              description:
                'Scegli il tipo di documento (es. contratto, busta paga, F24). Alcuni tipi richiedono un dipendente di riferimento.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="document-employee"]',
            popover: {
              title: 'Dipendente',
              description:
                'Seleziona il dipendente a cui è intestato il documento. Non richiesto per documenti aziendali generici come gli F24.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="document-dropzone"]',
            popover: {
              title: 'File',
              description:
                'Trascina i file o clicca per selezionarli. Sono accettati PDF, immagini e documenti Office.',
              side: 'left',
              align: 'center',
            },
          },
          {
            element: '[data-tour="document-save"]',
            popover: {
              title: 'Conferma',
              description: 'Clicca «Carica» per caricare il documento.',
              side: 'left',
              align: 'end',
              showButtons: ['previous', 'close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
        ],
      },
    ],
  },
  {
    routePrefix: '/employees',
    dialogTitle: 'Tutorial · Personale',
    dialogDescription: DEFAULT_DIALOG_DESCRIPTION,
    tutorials: [
      {
        id: TutorialId.Overview,
        label: TutorialLabel.Overview,
        steps: [
          {
            element: 'app-page-header',
            popover: {
              title: 'Benvenuto nella gestione del Personale',
              description:
                'Da qui crei, modifichi e gestisci i dipendenti dell\'azienda.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: 'app-employees-actions',
            popover: {
              title: 'Cerca un dipendente',
              description:
                'Filtra per ruolo, stato o email, oppure usa la ricerca per nome o codice fiscale.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: 'app-employees-table',
            popover: {
              title: 'L\'elenco dei dipendenti',
              description:
                'Ogni riga è un dipendente: aprine il profilo, attivalo o disattivalo, o invia di nuovo l\'invito dal menu a destra.',
              side: 'top',
              align: 'center',
            },
          },
        ],
      },
      {
        id: TutorialId.CreationGuide,
        label: TutorialLabel.CreationGuide,
        description: 'Ti guidiamo nella creazione di un nuovo dipendente.',
        requiresElement: '[data-tour="employees-create"]',
        steps: [
          {
            element: '[data-tour="employees-create"]',
            popover: {
              title: 'Crea un dipendente',
              description:
                'Clicca «Crea»: il tour avanzerà da solo non appena il pannello si apre.',
              side: 'bottom',
              align: 'end',
              showButtons: ['close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
          {
            element: '[data-tour="employee-firstname"]',
            popover: {
              title: 'Nome',
              description: 'Il nome del dipendente. È un campo obbligatorio.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-lastname"]',
            popover: {
              title: 'Cognome',
              description: 'Il cognome del dipendente. È un campo obbligatorio.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-email"]',
            popover: {
              title: 'Email',
              description: 'L\'email a cui verrà inviato l\'invito ad attivare l\'account.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-fiscal-code"]',
            popover: {
              title: 'Codice fiscale',
              description:
                '16 caratteri. Serve per associare correttamente cedolini e documenti.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-role"]',
            popover: {
              title: 'Ruolo',
              description:
                'Definisce i permessi nell\'app. In base al ruolo possono apparire campi aggiuntivi (es. Manager, Partita IVA).',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-phone"]',
            popover: {
              title: 'Telefono',
              description: 'Numero di telefono opzionale.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-manager"]',
            popover: {
              title: 'Manager diretto',
              description:
                'Il responsabile a cui il dipendente fa capo, usato per inoltrare le richieste di approvazione di assenze e rimborsi. Non richiesto per gli amministratori.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-mileage-cost"]',
            popover: {
              title: 'Costo chilometrico',
              description:
                'Scegli come calcolare il rimborso chilometrico per le note spese di carburante: manuale, tabelle ACI, costo aziendale o personalizzato. In base alla scelta possono apparire campi aggiuntivi.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="employee-save"]',
            popover: {
              title: 'Conferma',
              description: 'Clicca «Crea» per creare il dipendente e inviare l\'invito via email.',
              side: 'left',
              align: 'end',
              showButtons: ['previous', 'close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
        ],
      },
    ],
  },
  {
    routePrefix: '/bacheca',
    dialogTitle: 'Tutorial · Bacheca',
    dialogDescription: DEFAULT_DIALOG_DESCRIPTION,
    tutorials: [
      {
        id: TutorialId.Overview,
        label: TutorialLabel.Overview,
        steps: [
          {
            element: 'app-page-header',
            popover: {
              title: 'Benvenuto in Bacheca',
              description:
                'Da qui crei e pubblichi annunci visibili a tutta l\'azienda.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: 'app-bacheca-actions',
            popover: {
              title: 'Cerca tra gli annunci',
              description:
                'Filtra per categoria, priorità o autore per trovare rapidamente un annuncio specifico.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: 'app-bacheca-table',
            popover: {
              title: 'Gli annunci pubblicati',
              description:
                'Ogni riga è un annuncio: modificalo o eliminalo dal menu a destra. Gli annunci in evidenza appaiono in cima.',
              side: 'top',
              align: 'center',
            },
          },
        ],
      },
      {
        id: TutorialId.CreationGuide,
        label: TutorialLabel.CreationGuide,
        description: 'Ti guidiamo nella creazione di un nuovo annuncio.',
        requiresElement: '[data-tour="bacheca-create"]',
        steps: [
          {
            element: '[data-tour="bacheca-create"]',
            popover: {
              title: 'Crea un annuncio',
              description:
                'Clicca «Nuovo Annuncio»: il tour avanzerà da solo non appena il pannello si apre.',
              side: 'bottom',
              align: 'end',
              showButtons: ['close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
          {
            element: '[data-tour="announcement-title"]',
            popover: {
              title: 'Titolo',
              description:
                'Inserisci un titolo chiaro: è quello che i destinatari leggeranno per primo.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="announcement-content"]',
            popover: {
              title: 'Contenuto',
              description: 'Il corpo dell\'annuncio. Cerca di essere conciso e diretto.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="announcement-category"]',
            popover: {
              title: 'Categoria',
              description:
                'Aiuta i destinatari a inquadrare subito l\'argomento dell\'annuncio (es. comunicazione HR, evento, normativa).',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="announcement-priority"]',
            popover: {
              title: 'Priorità',
              description:
                'Imposta l\'urgenza dell\'annuncio. Le priorità alte sono più evidenti nella bacheca.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="announcement-tags"]',
            popover: {
              title: 'Tag',
              description:
                'Etichette opzionali separate da virgola per facilitare la ricerca degli annunci (es. importante, urgente).',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="announcement-expires"]',
            popover: {
              title: 'Data di scadenza',
              description:
                'Data opzionale oltre la quale l\'annuncio non sarà più visibile in bacheca.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="announcement-pinned"]',
            popover: {
              title: 'Fissa in alto',
              description:
                'Spuntalo per tenere l\'annuncio in cima alla bacheca, indipendentemente dalla data di pubblicazione.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="announcement-save"]',
            popover: {
              title: 'Conferma',
              description: 'Clicca «Crea» per pubblicare l\'annuncio.',
              side: 'left',
              align: 'end',
              showButtons: ['previous', 'close'],
            },
            onHighlighted: (el, _step, opts) => advanceOnClick(el, opts.driver),
          },
        ],
      },
    ],
  },
  {
    routePrefix: '/settings',
    dialogTitle: 'Tutorial · Impostazioni',
    dialogDescription: DEFAULT_DIALOG_DESCRIPTION,
    tutorials: [
      {
        id: TutorialId.Overview,
        label: TutorialLabel.Overview,
        steps: [
          {
            element: 'app-page-header',
            popover: {
              title: 'Benvenuto nelle Impostazioni',
              description:
                'Da qui gestisci il tuo profilo, la password e, se sei HR o Admin, i dati dell\'azienda e del consulente del lavoro.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[hlmTabsContent="profile"]',
            popover: {
              title: 'Il tuo profilo',
              description:
                'Aggiorna i tuoi dati personali, di contatto e l\'avatar. Le modifiche sono visibili solo a te e agli amministratori.',
              side: 'top',
              align: 'start',
            },
            onHighlightStarted: activateTab('[hlmTabsTrigger="profile"]'),
          },
          {
            element: '[hlmTabsContent="password"]',
            popover: {
              title: 'Cambia password',
              description:
                'Imposta una nuova password. Per sicurezza dovrai inserire prima quella attuale.',
              side: 'top',
              align: 'start',
            },
            onHighlightStarted: activateTab('[hlmTabsTrigger="password"]'),
          },
          {
            element: '[hlmTabsContent="company"]',
            popover: {
              title: 'Dati azienda',
              description:
                'Configura ragione sociale, indirizzo, P.IVA, logo e gli altri dati anagrafici dell\'azienda. Modificabile da HR e Admin.',
              side: 'top',
              align: 'start',
            },
            onHighlightStarted: activateTab('[hlmTabsTrigger="company"]'),
          },
          {
            element: '[hlmTabsContent="consultant"]',
            popover: {
              title: 'Consulente del lavoro',
              description:
                'Associa o disassocia il consulente del lavoro che gestisce i cedolini per la tua azienda.',
              side: 'top',
              align: 'start',
            },
            onHighlightStarted: activateTab('[hlmTabsTrigger="consultant"]'),
          },
        ],
      },
    ],
  },
];

/**
 * Global welcome tour shown the first time a user accesses the app.
 *
 * Strategy: the app is used only by HR/Admin and Labor Consultant,
 * and the sidebar nav items differ per role. Instead of one step per
 * link, we highlight the whole nav block in a single grouped step
 * and dedicate per-step coverage only to items shared across roles
 * (logo, settings, footer) plus the conditional tenant selector.
 *
 * Steps with a missing `element` selector are filtered out by the
 * service before driver.js sees them, so the tenant step is dropped
 * automatically for users without the multi-tenant header.
 */
export const WELCOME_TOUR: Tour = {
  steps: [
    {
      element: '[data-tour="sidebar-logo"]',
      popover: {
        title: 'Benvenuto in Pagai 👋',
        description:
          'Ti facciamo fare un giro veloce dell\'interfaccia. Puoi sempre riaprire questa guida cliccando sul logo Pagai.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="sidebar-tenant"]',
      popover: {
        title: 'Selettore azienda',
        description:
          'Se gestisci più aziende, da qui puoi passare dall\'una all\'altra.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="sidebar-nav"]',
      popover: {
        title: 'Le tue sezioni principali',
        description:
          'Qui trovi tutte le aree dell\'app: cedolini, rimborsi, presenze e assenze, documenti, e, in base al tuo ruolo, gestione personale, bacheca o aziende clienti.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="sidebar-user"]',
      popover: {
        title: 'Menu utente',
        description:
          'Cliccando qui accedi a Impostazioni (profilo, password e dati aziendali), Supporto e Logout.',
        side: 'right',
        align: 'end',
      },
    },
    {
      element: '[data-tour="page-tutorial-btn"]',
      popover: {
        title: 'Tutorial della pagina',
        description:
          'In ogni sezione trovi questo pulsante in basso a destra. Cliccalo per aprire i tutorial interattivi disponibili per la pagina corrente.',
        side: 'left',
        align: 'end',
      },
    },
  ],
  stagePadding: 6,
};
